import { PidNode } from './PidNode'
import { cx, TagLabel, type PidBaseProps } from './shared'

export interface TankProps extends PidBaseProps {
  /** 0–1 fill fraction. */
  level?: number
  value?: number
  unit?: string
  width?: number
  height?: number
  orientation?: 'vertical' | 'horizontal'
  layout?: 'port' | 'stack'
}

export function Tank({
  level = 0.5,
  value,
  unit = '%',
  width = 56,
  height = 80,
  orientation = 'vertical',
  layout = 'port',
  tag,
  className,
  style,
  title
}: TankProps) {
  const clipped = Math.max(0, Math.min(1, level))
  const horizontal = orientation === 'horizontal'
  const w = horizontal ? Math.max(width, height) : width
  const h = horizontal ? Math.min(width, height) : height
  const pad = 3
  const clipId = `tank-clip-${tag ?? 'x'}-${w}x${h}`
  const display = value ?? clipped * 100

  const glyph = (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <rect
            x={pad}
            y={pad}
            width={w - pad * 2}
            height={h - pad * 2}
            rx={horizontal ? h / 2 - pad : 10}
          />
        </clipPath>
      </defs>
      <rect
        className="pid-fill pid-stroke"
        x={pad}
        y={pad}
        width={w - pad * 2}
        height={h - pad * 2}
        rx={horizontal ? h / 2 - pad : 10}
        strokeWidth={1.5}
      />
      <rect
        className="pid-tank-liquid"
        x={pad}
        y={pad + (h - pad * 2) * (1 - clipped)}
        width={w - pad * 2}
        height={(h - pad * 2) * clipped}
        clipPath={`url(#${clipId})`}
      />
      {[0.25, 0.5, 0.75].map((t) => (
        <line
          key={t}
          className="pid-muted-stroke"
          x1={w - pad - 8}
          y1={pad + (h - pad * 2) * (1 - t)}
          x2={w - pad}
          y2={pad + (h - pad * 2) * (1 - t)}
          strokeWidth={1}
        />
      ))}
    </svg>
  )

  return (
    <div style={style} title={title ?? tag} className={cx(className)}>
      <PidNode glyph={glyph} glyphHeight={h} mode={layout}>
        <div className="pid-readout">
          <span className="pid-readout-value">{display.toFixed(0)}</span>
          <span className="pid-readout-unit">{unit}</span>
        </div>
        <TagLabel>{tag}</TagLabel>
      </PidNode>
    </div>
  )
}
